import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Row, Col, Select, Button, DatePicker } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { FileExcelOutlined, PrinterOutlined } from '@ant-design/icons';
import { useReactToPrint } from 'react-to-print';
import startCase from 'lodash/startCase';
import fileDownload from 'js-file-download';
import moment from 'moment';

import styles from '../styles';
import Breadcrumbs from '../Components/Breadcrumbs';
import {
  getCustomerIdsForOutwardReport,
  getJobchartIdsForOutwardReport,
  getProcessesForOutwardReport,
  getVendorIdsForOutwardReport,
  getRPOIdsForOutwardReport,
  clearCustomerIdsForOutwardReport,
  clearJobchartIdsForOutwardReport,
  clearProcessesForOutwardReport,
  clearVendorIdsForOutwardReport,
  clearRPOIdsForOutwardReport,
  getOutwardReport,
  clearOutwardReport,
} from '../Actions';
import Api from '../Api';
import { errorNotificationOnBlob } from '../Snackbar';

const { Option } = Select;

const OutwardReport = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const printRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: '',
  });

  const customerIdsForOutwardReport = useSelector(
    state => state.customerIdsForOutwardReport
  );
  const jobchartIdsForOutwardReport = useSelector(
    state => state.jobchartIdsForOutwardReport
  );
  const processesForOutwardReport = useSelector(
    state => state.processesForOutwardReport
  );
  const vendorIdsForOutwardReport = useSelector(
    state => state.vendorIdsForOutwardReport
  );
  const rpoIdsForOutwardReport = useSelector(
    state => state.rpoIdsForOutwardReport
  );
  const outwardReport = useSelector(state => state.outwardReport);

  const [customerId, setCustomerId] = useState(null);
  const [jobchartId, setJobchartId] = useState(null);
  const [process, setProcess] = useState(null);
  const [vendorId, setVendorId] = useState(null);
  const [rpoId, setRpoId] = useState(null);
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);

  useEffect(() => {
    dispatch(getCustomerIdsForOutwardReport());
    dispatch(getJobchartIdsForOutwardReport());
    dispatch(getProcessesForOutwardReport());
    dispatch(getVendorIdsForOutwardReport());
    dispatch(getRPOIdsForOutwardReport());
    return () => {
      dispatch(clearCustomerIdsForOutwardReport());
      dispatch(clearJobchartIdsForOutwardReport());
      dispatch(clearProcessesForOutwardReport());
      dispatch(clearVendorIdsForOutwardReport());
      dispatch(clearRPOIdsForOutwardReport());
      dispatch(clearOutwardReport());
    };
  }, []);

  useEffect(() => {
    dispatch(
      getOutwardReport({
        customerId,
        jobchartId,
        process,
        vendorId,
        rpoId,
        from,
        to,
      })
    );
  }, [customerId, jobchartId, process, vendorId, rpoId, from, to]);

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Customer',
        dataIndex: 'customer',
      },
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
      },
      {
        title: 'Process',
        dataIndex: 'process',
        render: text => startCase(text),
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
      },
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
      },
      {
        title: 'Outward #',
        dataIndex: 'outwardNo',
      },
      {
        title: 'Outward Date',
        dataIndex: 'outwardDate',
        render: outgoingDate =>
          outgoingDate ? moment(outgoingDate).format('MM/DD/YYYY') : null,
      },
      {
        title: 'Item Type',
        dataIndex: 'type',
      },
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Item',
        dataIndex: 'item',
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
    ];

    return column;
  }, []);

  const summaryColumns = useMemo(() => {
    const column = [
      {
        title: 'Process',
        dataIndex: 'process',
        render: text => startCase(text),
      },
      {
        title: 'Type',
        dataIndex: 'type',
      },
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Item',
        dataIndex: 'item',
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
      },
    ];

    return column;
  }, []);

  const itemRowKey = useMemo(
    () => record =>
      `BPO${record.bpoNo}RPO${record.rpoNo}Outward${record.outwardNo}Pr${record.process}T${record.type}It${record.item}Qty${record.quantity}`,
    []
  );

  const summaryRowKey = useMemo(
    () => record =>
      `Pr${record.process}T${record.type}It${record.item}Qty${record.quantity}`,
    []
  );

  const exportExcelFile = useCallback(async () => {
    try {
      const { data } = await Api.getFile(
        `/store-outwards/export/outwardreport`,
        {
          customerId,
          jobchartId,
          process,
          vendorId,
          rpoId,
          from,
          to,
        }
      );
      fileDownload(
        data,
        `outward-report-${moment().format('DD MMM YYYY')}.csv`
      );
    } catch (error) {
      errorNotificationOnBlob(error);
    }
  }, [customerId, jobchartId, process, vendorId, rpoId, from, to]);

  return (
    <>
      <Breadcrumbs
        moduleName="Outward Report"
        moduleLink="/outwardreport"
        subModuleName=""
      />
      <Row className={classes.paddingToContainerContent}>
        <Col className={classes.marginBetweenSelect}>
          <div>Customer</div>
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select customer"
            optionFilterProp="children"
            onChange={value => {
              setCustomerId(value);
              dispatch(
                getJobchartIdsForOutwardReport({
                  customerId: value,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getProcessesForOutwardReport({
                  customerId: value,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getVendorIdsForOutwardReport({
                  customerId: value,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getRPOIdsForOutwardReport({
                  customerId: value,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId,
                })
              );
            }}
          >
            {customerIdsForOutwardReport.map(customer => (
              <Option value={customer.id} key={customer.id}>
                {customer.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>BPO #</div>
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select BPO #"
            optionFilterProp="children"
            onChange={value => {
              setJobchartId(value);
              dispatch(
                getCustomerIdsForOutwardReport({
                  customerId,
                  jobchartId: value,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getProcessesForOutwardReport({
                  customerId,
                  jobchartId: value,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getVendorIdsForOutwardReport({
                  customerId,
                  jobchartId: value,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getRPOIdsForOutwardReport({
                  customerId,
                  jobchartId: value,
                  process,
                  vendorId,
                  rpoId,
                })
              );
            }}
          >
            {jobchartIdsForOutwardReport.map(jobchart => (
              <Option value={jobchart.id} key={jobchart.id}>
                {jobchart.bpoNo}
              </Option>
            ))}
          </Select>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>Process</div>
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select Process"
            optionFilterProp="children"
            onChange={value => {
              setProcess(value);
              dispatch(
                getCustomerIdsForOutwardReport({
                  customerId,
                  jobchartId,
                  process: value,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getJobchartIdsForOutwardReport({
                  customerId,
                  jobchartId,
                  process: value,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getVendorIdsForOutwardReport({
                  customerId,
                  jobchartId,
                  process: value,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getRPOIdsForOutwardReport({
                  customerId,
                  jobchartId,
                  process: value,
                  vendorId,
                  rpoId,
                })
              );
            }}
          >
            {processesForOutwardReport.map(text => (
              <Option value={text.process} key={text.process}>
                {startCase(text.process)}
              </Option>
            ))}
          </Select>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>Vendor</div>
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select vendor"
            optionFilterProp="children"
            onChange={value => {
              setVendorId(value);
              dispatch(
                getCustomerIdsForOutwardReport({
                  customerId,
                  jobchartId,
                  process,
                  vendorId: value,
                  rpoId,
                })
              );
              dispatch(
                getJobchartIdsForOutwardReport({
                  customerId,
                  jobchartId,
                  process,
                  vendorId: value,
                  rpoId,
                })
              );
              dispatch(
                getProcessesForOutwardReport({
                  customerId,
                  jobchartId,
                  process,
                  vendorId: value,
                  rpoId,
                })
              );
              dispatch(
                getRPOIdsForOutwardReport({
                  customerId,
                  jobchartId,
                  process,
                  vendorId: value,
                  rpoId,
                })
              );
            }}
          >
            {vendorIdsForOutwardReport.map(vendor => (
              <Option value={vendor.id} key={vendor.id}>
                {vendor.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>RPO #</div>
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select RPO #"
            optionFilterProp="children"
            onChange={value => {
              setRpoId(value);
              dispatch(
                getCustomerIdsForOutwardReport({
                  customerId,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId: value,
                })
              );
              dispatch(
                getJobchartIdsForOutwardReport({
                  customerId,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId: value,
                })
              );
              dispatch(
                getProcessesForOutwardReport({
                  customerId,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId: value,
                })
              );
              dispatch(
                getVendorIdsForOutwardReport({
                  customerId,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId: value,
                })
              );
            }}
          >
            {rpoIdsForOutwardReport.map(rpo => (
              <Option value={rpo.id} key={rpo.id}>
                {rpo.rpoNo}
              </Option>
            ))}
          </Select>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>From </div>
          <DatePicker
            placeholder="From Date"
            onChange={value => setFrom(value ? value.toISOString() : null)}
          />
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>To</div>
          <DatePicker
            placeholder="To Date"
            onChange={value => setTo(value ? value.toISOString() : null)}
          />
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>Print</div>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>Export</div>
          <Button icon={<FileExcelOutlined />} onClick={exportExcelFile}>
            Export
          </Button>
        </Col>
      </Row>
      <br />
      <div className={classes.tableStyle} ref={printRef}>
        <h2>Outward Report</h2>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={outwardReport.items}
          rowKey={itemRowKey}
          pagination={false}
          scroll={false}
          title={() => 'Items'}
        />
        <br />
        <Table
          bordered
          size="small"
          columns={summaryColumns}
          dataSource={outwardReport.summary}
          rowKey={summaryRowKey}
          pagination={false}
          scroll={false}
          title={() => 'Items Summary'}
        />
      </div>
    </>
  );
};

OutwardReport.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(OutwardReport);
